import { createLogger } from "logging";
import { ConfirmDeleteTarget } from "models/ConfirmDeleteTarget";
import { TaskModel } from "models/TaskModel";
import { ToggleType } from "models/ToggleType";
import { useMeeting } from "providers";
import React, { PropsWithChildren, startTransition, useCallback, useEffect, useMemo, useState } from "react";

import { ConfirmDeleteTask } from "./components/ConfirmDeleteTask";
import { TaskContext } from "./TaskContext";

const logger = createLogger("TaskProvider");

export const TaskProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { meeting } = useMeeting();

  const [deleting, setDeleting] = useState<ToggleType>({});
  const [updating, setUpdating] = useState<ToggleType>({});
  const [confirmDeleteTask, setConfirmDeleteTask] = useState<ConfirmDeleteTarget | undefined>(undefined);
  const [selectTaskId, setSelectTaskId] = useState<number>(-1);
  const [tasks, setTasks] = useState<TaskModel[]>([]);

  const handleSetTasks = useCallback(
    (itemsToSet: TaskModel[]) => {
      setTasks(itemsToSet);
      logger.trace("handleSetTasks(): items:=", itemsToSet);

      const newSelectedId = itemsToSet.find((x) => x.id === selectTaskId)
        ? selectTaskId
        : itemsToSet.length > 0
          ? itemsToSet[0].id
          : -1;

      setSelectTaskId(newSelectedId);
      logger.trace("handleSetTasks(): selected:=", newSelectedId);
    },
    [selectTaskId]
  );

  useEffect(() => {
    startTransition(() => {
      handleSetTasks(meeting?.tasks ?? []);
    });
  }, [meeting, handleSetTasks]);

  const isTaskUpdating = useCallback(
    (id: number): boolean => {
      const flag = updating[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [updating]
  );

  const isTaskDeleting = useCallback(
    (id: number): boolean => {
      const flag = deleting[id];
      if (flag !== undefined) return flag;
      return false;
    },
    [deleting]
  );

  const setTaskUpdating = useCallback((id: number, newState: boolean) => {
    setUpdating((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const setTaskDeleting = useCallback((id: number, newState: boolean) => {
    setDeleting((prev) => ({ ...prev, [id]: newState }));
  }, []);

  const contextValue = useMemo(
    () => ({
      tasks,
      confirmDeleteTask,
      setConfirmDeleteTask,
      selectTaskId,
      setSelectTaskId,
      isTaskUpdating,
      setTaskUpdating,
      isTaskDeleting,
      setTaskDeleting,
    }),
    [
      tasks,
      confirmDeleteTask,
      setConfirmDeleteTask,
      selectTaskId,
      setSelectTaskId,
      isTaskUpdating,
      setTaskUpdating,
      isTaskDeleting,
      setTaskDeleting,
    ]
  );

  return (
    <TaskContext.Provider value={contextValue}>
      {children}
      <ConfirmDeleteTask />
    </TaskContext.Provider>
  );
};
